import {
  DEFAULT_COLLISION_PADDING,
  getNodeSize,
  getSubtreeIds,
  resolveMoveCommandCollisions,
  topLeftRectsOverlap,
} from './mapCommands';
import { createSpatialIndex } from './spatialIndex';
import {
  hasOverlappingRoutingNodes,
  inflateRect,
  MAX_OBSTACLE_ROUTING_NODES,
  routeCrossesRoute,
  routeEdges,
  routeIntersectsRect,
} from './edgeRouting';

export const DEFAULT_SETTLE_STEP = 24;
export const DEFAULT_MAX_SETTLE_SHIFT = 96;
export const DEFAULT_MAX_SETTLED_BRANCHES = 2;
const DEFAULT_MAX_STATIONARY_CANDIDATES = 8;

function finite(value, fallback = 0) {
  return Number.isFinite(value) ? value : fallback;
}

function relationshipKey(relationship) {
  return `${relationship.parentId}::${relationship.childId}`;
}

function runtimeNodeSize(node, nodeSizes) {
  const runtime = nodeSizes instanceof Map
    ? nodeSizes.get(node.id)
    : nodeSizes?.[node.id];
  return getNodeSize(runtime ? { ...node, layoutSize: runtime } : node);
}

function nodeRect(node, nodeSizes) {
  return {
    x: finite(node?.position?.x),
    y: finite(node?.position?.y),
    ...runtimeNodeSize(node, nodeSizes),
  };
}

function effectiveRelationships(relationships, command) {
  const safeRelationships = Array.isArray(relationships) ? relationships : [];
  if (command?.type !== 'reparent-subtree') return safeRelationships;
  const removedKey = command.removeRelationship
    ? relationshipKey(command.removeRelationship)
    : null;
  return [
    ...safeRelationships.filter(
      (relationship) => relationshipKey(relationship) !== removedKey
    ),
    command.relationship,
  ].filter((relationship) => relationship?.parentId && relationship?.childId);
}

function applyPositions(nodes, positions) {
  const updates = new Map(
    (positions || []).map((entry) => [entry.id, entry.position])
  );
  return nodes.map((node) => (
    updates.has(node.id)
      ? { ...node, position: { ...updates.get(node.id) } }
      : node
  ));
}

function compareConflicts(left, right) {
  return left.key.localeCompare(right.key);
}

/**
 * Validate the complete derived route snapshot. The returned geometry is
 * deterministic and remains display-only; only node positions may be written.
 */
export function inspectSettledGeometry({
  nodes = [],
  relationships = [],
  nodeSizes = null,
  previousRoutes = new Map(),
} = {}) {
  const routeNodes = nodes.map((node) => ({
    id: node.id,
    rect: nodeRect(node, nodeSizes),
  }));
  const rects = new Map(routeNodes.map((node) => [node.id, node.rect]));
  const orderedRelationships = [...relationships]
    .filter((relationship) => rects.has(relationship.parentId)
      && rects.has(relationship.childId))
    .sort((left, right) => (
      String(left.parentId).localeCompare(String(right.parentId))
      || String(left.childId).localeCompare(String(right.childId))
    ));
  const routes = routeEdges({
    nodes: routeNodes,
    edges: orderedRelationships,
    previousRoutes,
  });
  const conflicts = [];

  orderedRelationships.forEach((relationship) => {
    const edgeKey = relationshipKey(relationship);
    const route = routes.get(edgeKey);
    if (!route) return;
    routeNodes.forEach((node) => {
      if (node.id === relationship.parentId || node.id === relationship.childId) return;
      if (!routeIntersectsRect(route, inflateRect(node.rect))) return;
      conflicts.push({
        type: 'node',
        key: `node:${edgeKey}:${node.id}`,
        edgeKeys: [edgeKey],
        nodeIds: [relationship.parentId, relationship.childId, node.id],
        obstacleNodeId: node.id,
      });
    });
  });

  for (let leftIndex = 0; leftIndex < orderedRelationships.length; leftIndex += 1) {
    const leftRelationship = orderedRelationships[leftIndex];
    const leftKey = relationshipKey(leftRelationship);
    const leftRoute = routes.get(leftKey);
    if (!leftRoute) continue;
    for (
      let rightIndex = leftIndex + 1;
      rightIndex < orderedRelationships.length;
      rightIndex += 1
    ) {
      const rightRelationship = orderedRelationships[rightIndex];
      const rightKey = relationshipKey(rightRelationship);
      const rightRoute = routes.get(rightKey);
      if (!rightRoute || !routeCrossesRoute(leftRoute, rightRoute)) continue;
      conflicts.push({
        type: 'edge',
        key: `edge:${leftKey}:${rightKey}`,
        edgeKeys: [leftKey, rightKey],
        nodeIds: [
          leftRelationship.parentId,
          leftRelationship.childId,
          rightRelationship.parentId,
          rightRelationship.childId,
        ],
      });
    }
  }

  conflicts.sort(compareConflicts);
  return {
    valid: conflicts.length === 0,
    conflictCount: conflicts.length,
    conflicts,
    routes,
  };
}

function createBranchCollisionChecker(nodes, branchIds, nodeSizes, padding) {
  const branchRects = nodes
    .filter((node) => branchIds.has(node.id))
    .map((node) => nodeRect(node, nodeSizes));
  const stationaryIndex = createSpatialIndex(
    nodes
      .filter((node) => !branchIds.has(node.id))
      .map((node) => nodeRect(node, nodeSizes))
  );
  return (offset) => branchRects.every((rect) => {
    const movedRect = {
      ...rect,
      x: rect.x + offset.x,
      y: rect.y + offset.y,
    };
    return stationaryIndex.query(movedRect, { padding }).every((other) => (
      !topLeftRectsOverlap(movedRect, other, padding)
    ));
  });
}

function moveBranch(nodes, branchIds, offset) {
  return nodes.map((node) => (
    branchIds.has(node.id)
      ? {
          ...node,
          position: {
            x: finite(node.position?.x) + offset.x,
            y: finite(node.position?.y) + offset.y,
          },
        }
      : node
  ));
}

function nodeCenter(node, nodeSizes) {
  const rect = nodeRect(node, nodeSizes);
  return {
    x: rect.x + rect.width / 2,
    y: rect.y + rect.height / 2,
  };
}

function conflictFocus(conflicts, nodesById, nodeSizes) {
  const ids = [...new Set(conflicts.flatMap((conflict) => conflict.nodeIds))].sort();
  const centers = ids.map((id) => nodesById.get(id))
    .filter(Boolean)
    .map((node) => nodeCenter(node, nodeSizes));
  if (centers.length === 0) return { x: 0, y: 0 };
  return {
    x: centers.reduce((sum, point) => sum + point.x, 0) / centers.length,
    y: centers.reduce((sum, point) => sum + point.y, 0) / centers.length,
  };
}

function offsetCandidates(rootCenter, focus, step, maxShift) {
  const away = {
    x: rootCenter.x - focus.x,
    y: rootCenter.y - focus.y,
  };
  const awayLength = Math.hypot(away.x, away.y) || 1;
  const offsets = [];
  for (let distance = step; distance <= maxShift; distance += step) {
    offsets.push(
      { x: distance, y: 0 },
      { x: -distance, y: 0 },
      { x: 0, y: distance },
      { x: 0, y: -distance },
      { x: distance, y: distance },
      { x: distance, y: -distance },
      { x: -distance, y: distance },
      { x: -distance, y: -distance },
    );
  }
  return offsets.map((offset) => {
    const length = Math.hypot(offset.x, offset.y);
    const alignment = (
      offset.x * away.x + offset.y * away.y
    ) / (length * awayLength);
    return {
      ...offset,
      distance: length,
      directionPenalty: -alignment,
    };
  }).sort((left, right) => (
    left.distance - right.distance
    || left.directionPenalty - right.directionPenalty
    || left.x - right.x
    || left.y - right.y
  ));
}

function stationaryRootCandidates({
  conflicts,
  nodes,
  relationships,
  movedIds,
  displacedIds,
  nodeSizes,
  maximum,
}) {
  const nodesById = new Map(nodes.map((node) => [node.id, node]));
  const childIds = new Set(relationships.map((relationship) => relationship.childId));
  const focus = conflictFocus(conflicts, nodesById, nodeSizes);
  const conflictIds = [...new Set(
    conflicts.flatMap((conflict) => conflict.nodeIds)
  )].sort();

  return conflictIds.map((id) => {
    const node = nodesById.get(id);
    const subtreeIds = new Set(getSubtreeIds(id, relationships));
    return {
      id,
      node,
      subtreeIds,
      subtreeSize: subtreeIds.size,
      pinned: node?.layout?.pinned === true,
      isGraphRoot: !childIds.has(id),
      center: node ? nodeCenter(node, nodeSizes) : { x: 0, y: 0 },
      focus,
    };
  }).filter((candidate) => (
    candidate.node
    && !candidate.isGraphRoot
    && [...candidate.subtreeIds].every((id) => !movedIds.has(id) && !displacedIds.has(id))
  )).sort((left, right) => (
    Number(left.pinned) - Number(right.pinned)
    || left.subtreeSize - right.subtreeSize
    || left.id.localeCompare(right.id)
  )).slice(0, maximum);
}

function compareMoves(left, right) {
  if (left.inspection.valid !== right.inspection.valid) {
    return left.inspection.valid ? -1 : 1;
  }
  if (left.inspection.valid) {
    return Number(left.root.pinned) - Number(right.root.pinned)
      || left.root.subtreeSize - right.root.subtreeSize
      || left.offset.distance - right.offset.distance
      || left.offset.directionPenalty - right.offset.directionPenalty
      || left.root.id.localeCompare(right.root.id)
      || left.offset.x - right.offset.x
      || left.offset.y - right.offset.y;
  }
  return left.inspection.conflictCount - right.inspection.conflictCount
    || Number(left.root.pinned) - Number(right.root.pinned)
    || left.root.subtreeSize - right.root.subtreeSize
    || left.offset.distance - right.offset.distance
    || left.offset.directionPenalty - right.offset.directionPenalty
    || left.root.id.localeCompare(right.root.id)
    || left.offset.x - right.offset.x
    || left.offset.y - right.offset.y;
}

function buildSettledCommand(command, originalNodes, finalNodes, settledRootIds, inspection) {
  const originalById = new Map(originalNodes.map((node) => [node.id, node]));
  const commandEntries = new Map(command.positions.map((entry) => [entry.id, entry]));
  const positionIds = new Set(commandEntries.keys());
  finalNodes.forEach((node) => {
    const original = originalById.get(node.id);
    if (
      original
      && (
        finite(node.position?.x) !== finite(original.position?.x)
        || finite(node.position?.y) !== finite(original.position?.y)
      )
    ) {
      positionIds.add(node.id);
    }
  });
  const finalById = new Map(finalNodes.map((node) => [node.id, node]));
  const positions = [...positionIds].sort().map((id) => ({
    id,
    position: { ...finalById.get(id).position },
    ...(commandEntries.get(id)?.layout
      ? { layout: commandEntries.get(id).layout }
      : {}),
  }));

  return {
    ...command,
    positions,
    collisionResolved: Boolean(command.collisionResolved),
    settlingValidated: inspection.valid,
    settlingResolved: settledRootIds.length > 0,
    settledBranchRootIds: settledRootIds,
    settlingConflictCount: inspection.conflictCount,
    settlingExhausted: !inspection.valid,
  };
}

/**
 * Resolve one completed drag against the canonical graph snapshot. Candidate
 * ordering is stable, subtree offsets are preserved, and every returned
 * position can be committed in one existing move/reparent transaction.
 */
export function settleReleasedSubtree(command, {
  nodes = [],
  relationships = [],
  nodeSizes = null,
  padding = DEFAULT_COLLISION_PADDING,
  step = DEFAULT_SETTLE_STEP,
  maxShift = DEFAULT_MAX_SETTLE_SHIFT,
  maxSettledBranches = DEFAULT_MAX_SETTLED_BRANCHES,
  maxStationaryCandidates = DEFAULT_MAX_STATIONARY_CANDIDATES,
  previousRoutes = new Map(),
  routedNodeIds = null,
} = {}) {
  const safeStep = Math.max(1, finite(step, DEFAULT_SETTLE_STEP));
  const safeMaxShift = Math.max(
    safeStep,
    finite(maxShift, DEFAULT_MAX_SETTLE_SHIFT)
  );
  const safeMaxSettledBranches = Math.max(
    0,
    Math.floor(finite(maxSettledBranches, DEFAULT_MAX_SETTLED_BRANCHES))
  );
  const safeMaxStationaryCandidates = Math.max(
    0,
    Math.floor(finite(
      maxStationaryCandidates,
      DEFAULT_MAX_STATIONARY_CANDIDATES
    ))
  );
  const collisionCommand = resolveMoveCommandCollisions(command, {
    nodes,
    nodeSizes,
    padding,
  });
  const routedRelationships = effectiveRelationships(relationships, collisionCommand);
  const movedIds = new Set(collisionCommand.positions.map((entry) => entry.id));
  const displacedIds = new Set();
  const settledRootIds = [];
  let currentNodes = applyPositions(nodes, collisionCommand.positions);
  const visibleIds = routedNodeIds instanceof Set
    ? routedNodeIds
    : (Array.isArray(routedNodeIds) ? new Set(routedNodeIds) : null);
  const boundedInspectionNodes = visibleIds
    ? currentNodes.filter((node) => visibleIds.has(node.id))
    : currentNodes;
  const boundedRouteNodes = boundedInspectionNodes.map((node) => ({
    id: node.id,
    rect: nodeRect(node, nodeSizes),
  }));
  const settlingFallback = boundedRouteNodes.length > MAX_OBSTACLE_ROUTING_NODES
    ? 'routing-complexity'
    : hasOverlappingRoutingNodes(boundedRouteNodes)
      ? 'overlapping-nodes'
      : null;

  // Complete edge-crossing settlement assumes a non-overlapping visibility
  // graph and expands many candidate routes while testing nearby branches.
  // When that precondition is false, retain only the bounded collision-safe
  // direct drop. The renderer already uses the matching smooth-route fallback,
  // so doing more synchronous work here cannot produce a valid untangled map.
  if (settlingFallback) {
    return {
      ...collisionCommand,
      settlingValidated: false,
      settlingResolved: false,
      settledBranchRootIds: [],
      settlingConflictCount: null,
      settlingExhausted: false,
      settlingSkipped: true,
      settlingFallback,
    };
  }
  const inspectCurrent = (candidateNodes, candidatePreviousRoutes) => {
    const inspectedNodes = visibleIds
      ? candidateNodes.filter((node) => visibleIds.has(node.id))
      : candidateNodes;
    const inspectedIds = new Set(inspectedNodes.map((node) => node.id));
    const inspectedRelationships = routedRelationships.filter(
      (relationship) => (
        inspectedIds.has(relationship.parentId)
        && inspectedIds.has(relationship.childId)
      )
    );
    return inspectSettledGeometry({
      nodes: inspectedNodes,
      relationships: inspectedRelationships,
      nodeSizes,
      previousRoutes: candidatePreviousRoutes,
    });
  };
  const initialInspection = inspectCurrent(currentNodes, previousRoutes);
  let inspection = initialInspection;

  for (
    let iteration = 0;
    !inspection.valid && iteration < safeMaxSettledBranches;
    iteration += 1
  ) {
    const roots = stationaryRootCandidates({
      conflicts: inspection.conflicts,
      nodes: currentNodes,
      relationships: routedRelationships,
      movedIds,
      displacedIds,
      nodeSizes,
      maximum: safeMaxStationaryCandidates,
    });
    let bestValidMove = null;
    let bestImprovement = null;
    for (const root of roots) {
      if (
        bestValidMove
        && (
          Number(root.pinned) > Number(bestValidMove.root.pinned)
          || (
            root.pinned === bestValidMove.root.pinned
            && root.subtreeSize > bestValidMove.root.subtreeSize
          )
        )
      ) {
        break;
      }
      const branchCollisionFreeAt = createBranchCollisionChecker(
        currentNodes,
        root.subtreeIds,
        nodeSizes,
        padding
      );
      for (const offset of offsetCandidates(
        root.center,
        root.focus,
        safeStep,
        safeMaxShift
      )) {
        if (!branchCollisionFreeAt(offset)) continue;
        const candidateNodes = moveBranch(currentNodes, root.subtreeIds, offset);
        const candidateInspection = inspectCurrent(
          candidateNodes,
          inspection.routes
        );
        if (
          !candidateInspection.valid
          && candidateInspection.conflictCount >= inspection.conflictCount
        ) {
          continue;
        }
        const move = {
          root,
          offset,
          nodes: candidateNodes,
          inspection: candidateInspection,
        };
        if (candidateInspection.valid) {
          if (!bestValidMove || compareMoves(move, bestValidMove) < 0) {
            bestValidMove = move;
          }
          // Offsets are ordered by distance and preferred direction. Once a
          // root has a valid option, later offsets cannot improve its primary
          // pinned/subtree score.
          break;
        }
        if (!bestImprovement || compareMoves(move, bestImprovement) < 0) {
          bestImprovement = move;
        }
      }
    }
    const selected = bestValidMove || bestImprovement;
    if (!selected) break;
    currentNodes = selected.nodes;
    inspection = selected.inspection;
    settledRootIds.push(selected.root.id);
    selected.root.subtreeIds.forEach((id) => displacedIds.add(id));
  }

  // A bounded solver should never leave behind a partial rearrangement that
  // did not actually produce a valid result. Keep the collision-safe direct
  // drop and report exhaustion for diagnostics/manual follow-up.
  if (!inspection.valid && settledRootIds.length > 0) {
    currentNodes = applyPositions(nodes, collisionCommand.positions);
    inspection = initialInspection;
    settledRootIds.splice(0);
  }

  return buildSettledCommand(
    collisionCommand,
    nodes,
    currentNodes,
    settledRootIds,
    inspection
  );
}
